import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Logo, LogoMark } from '@/shared/ui/logo';
import { LoadingStatus } from '@/shared/ui/spinner';

import { formatDate } from '../billing-format';

import { PlanCards } from './plan-cards';

import type { BillingPlanId, BillingStatus } from '@mio/shared';

import '../account.css';

function headline(status: BillingStatus) {
  const { access, trial, subscription, pass } = status;
  if (access.state === 'suspended')
    return {
      title: 'Your account is suspended',
      detail: 'Contact support to restore access. You can still manage billing below.',
    };
  if (trial && !subscription && !pass)
    return {
      title: 'Your free trial has ended',
      detail: `Your ${trial.source === 'work_email' ? '10' : '3'}-day trial ended ${access.expiresAt ? formatDate(access.expiresAt) : 'recently'}. Choose a plan to keep your progress and continue the residency.`,
    };
  return {
    title: 'Your access has ended',
    detail: access.expiresAt
      ? `Access ended ${formatDate(access.expiresAt)}. Choose a plan to continue where you left off.`
      : 'Choose a plan to continue where you left off.',
  };
}

/** Full-screen replacement for the app while paid access is required. */
export function AccessLocked({
  status,
  busyPlan,
  confirming,
  error,
  signingOut,
  onSelect,
  onPortal,
  portalBusy,
  onSignOut,
}: {
  status: BillingStatus;
  busyPlan: BillingPlanId | null;
  confirming: boolean;
  error: string | null;
  signingOut: boolean;
  onSelect: (plan: BillingPlanId) => void;
  onPortal: () => void;
  portalBusy: boolean;
  onSignOut: () => void;
}) {
  const copy = headline(status);
  return (
    <main className="bg-canvas text-fg flex min-h-dvh items-start justify-center px-5 py-10 sm:items-center">
      <div className="access-locked">
        <span className="flex items-center gap-2">
          <LogoMark size={28} decorative />
          <Logo height={22} />
        </span>
        <header className="access-locked-heading">
          <h1>{copy.title}</h1>
          <p>{copy.detail}</p>
        </header>
        {confirming ? <LoadingStatus label="Confirming your payment…" /> : null}
        {error ? <Alert>{error}</Alert> : null}
        <PlanCards
          plans={status.plans}
          status={status}
          busyPlan={busyPlan}
          disabled={confirming || status.access.state === 'suspended'}
          onSelect={onSelect}
        />
        <footer className="access-locked-footer">
          {status.canManageBilling ? (
            <Button type="button" variant="secondary" onClick={onPortal} loading={portalBusy}>
              Manage billing
            </Button>
          ) : null}
          <Button type="button" variant="ghost" onClick={onSignOut} loading={signingOut}>
            Sign out
          </Button>
        </footer>
      </div>
    </main>
  );
}
